import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Modal,
  LinearProgress,
} from '@mui/material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  BarChart as BarChartIcon,
  Mail as MailIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { ContactModal, Contact } from '../contacts/ContactModal';
import { EmailDetail } from '../gmail/EmailDetail';
import { EmailCompose } from '../gmail/EmailCompose';
import { useDatabase } from '../../hooks/useDatabase';
import { useGmail } from '../../hooks/useGmail';
import { Initiative, Task, Event } from '../../types/database';
import { EmailMetadata, EmailDetailData } from '../../types/gmail';
import { EntityValence } from '../visualizations/EntityValence';
import { QuantUpdates } from './QuantUpdates';
import { AskQuant } from './AskQuant';
import { OnboardingChecklist } from '../onboarding/OnboardingChecklist';
import { useUserProfile } from '../../hooks/useUserProfile';

interface ActivityItem {
  id: string;
  type: 'contact' | 'initiative' | 'event' | 'task';
  action: string;
  name: string;
  timestamp: string;
  metadata?: { contact_id?: string };
}

type GoalCategory = 'financial' | 'professional' | 'personal';
type GoalRecord = {
  id: string;
  category: GoalCategory;
  title: string | null;
};

export function Dashboard() {
  const { user, viewingAsUserId } = useAuth();
  const navigate = useNavigate();
  const db = useDatabase();
  const { listEmails, checkConnection, getEmail } = useGmail();
  const supabase = useSupabaseClient();
  const [openContactModal, setOpenContactModal] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [importantEmails, setImportantEmails] = useState<EmailMetadata[]>([]);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [incompleteItemsLimit, setIncompleteItemsLimit] = useState(5);
  const [recentContactsLimit, setRecentContactsLimit] = useState(5);
  const [activeContactsLimit, setActiveContactsLimit] = useState(5);
  const [topInitiativesLimit, setTopInitiativesLimit] = useState(5);
  const [recentActivityLimit, setRecentActivityLimit] = useState(5);
  const [openValenceModal, setOpenValenceModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailMetadata | null>(null);
  const [emailDetailOpen, setEmailDetailOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState<EmailMetadata | null>(null);
  const [forwardToEmail, setForwardToEmail] = useState<EmailMetadata | null>(null);
  const [forwardToEmailDetail, setForwardToEmailDetail] = useState<EmailDetailData | null>(null);
  const [userGoals, setUserGoals] = useState<GoalRecord[]>([]);
  const profileState = useUserProfile();
  const {
    profile: userProfile,
    setGmailConnected: updateProfileGmailStatus,
  } = profileState;

  const loadUserGoals = useCallback(async () => {
    const effectiveUserId = viewingAsUserId || user?.id;
    if (!effectiveUserId) {
      setUserGoals([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('goals')
        .select('id, category, title')
        .eq('user_id', effectiveUserId);

      if (error) {
        throw error;
      }

      setUserGoals((data ?? []) as GoalRecord[]);
    } catch (goalError) {
      console.error('Failed to load user goals:', goalError);
    }
  }, [supabase, user?.id, viewingAsUserId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use current profile state, but don't depend on it to avoid re-renders
      const currentProfile = userProfile;
      const shouldCheckGmail = currentProfile?.gmail_connected !== false;
      
      // Parallelize all data fetches including Gmail check
      const dataPromises: Promise<any>[] = [
        db.getContacts(),
        db.getInitiatives(),
        db.getTasks(),
        db.getEvents(),
      ];

      // Add Gmail check to parallel execution if needed
      if (shouldCheckGmail) {
        dataPromises.push(checkConnection());
      }

      // Execute all fetches in parallel
      const results = await Promise.all(dataPromises);

      // Extract and set main data
      const [contactsResult, initiativesResult, tasksResult, eventsResult, ...gmailResults] = results;

      // Check for errors and set state for main data
      if (contactsResult.error) throw contactsResult.error;
      setContacts(contactsResult.data || []);

      if (initiativesResult.error) throw initiativesResult.error;
      setInitiatives(initiativesResult.data || []);

      if (tasksResult.error) throw tasksResult.error;
      setTasks(tasksResult.data || []);

      if (eventsResult.error) throw eventsResult.error;
      setEvents(eventsResult.data || []);

      // Handle Gmail connection and emails
      if (shouldCheckGmail && gmailResults.length > 0) {
        const connectionStatus = gmailResults[0] as Awaited<ReturnType<typeof checkConnection>>;
        setGmailConnected(connectionStatus.isConnected);
        
        // Only update profile if status actually changed to avoid loops
        if (currentProfile && currentProfile.gmail_connected !== connectionStatus.isConnected) {
          await updateProfileGmailStatus(connectionStatus.isConnected);
        }
        
        // Load emails if connected (this can be done after main data is displayed)
        if (connectionStatus.isConnected) {
          listEmails({ 
            query: 'is:important',
            maxResults: 5 
          })
            .then(({ emails }) => {
              setImportantEmails(emails);
            })
            .catch((emailError) => {
              console.error('Failed to load important emails:', emailError);
              // Don't throw error for email loading failure
            });
        }
      } else {
        setGmailConnected(false);
        setImportantEmails([]);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [db, checkConnection, listEmails, updateProfileGmailStatus, userProfile]);

  // Load data on mount, when user changes, or when viewing as changes
  useEffect(() => {
    if (user?.id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, viewingAsUserId]);

  useEffect(() => {
    loadUserGoals();
  }, [loadUserGoals]);


  const handleContactClick = (contactId: string) => {
    navigate(`/contacts/${contactId}`);
  };

  const handleAddContact = async (formData: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'relevance_score'>>, organizationId?: string | null) => {
    try {
      const { data: newContact, error } = await db.createContact(formData);
      if (error) throw error;
      
      // Link organization if provided
      if (newContact && organizationId) {
        await db.linkContactToOrganization(newContact.id, organizationId);
      }
      
      await loadData(); // Reload data to get new data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    }
  };

  const handleEmailClick = (email: EmailMetadata) => {
    setSelectedEmail(email);
    setEmailDetailOpen(true);
  };

  const handleEmailDetailClose = () => {
    setEmailDetailOpen(false);
    setSelectedEmail(null);
  };

  const handleReply = (email: EmailMetadata) => {
    setReplyToEmail(email);
    setForwardToEmail(null);
    setEmailDetailOpen(false);
    setComposeOpen(true);
  };

  const handleForward = async (email: EmailMetadata, emailDetail?: EmailDetailData | null) => {
    setForwardToEmail(email);
    setReplyToEmail(null);
    setEmailDetailOpen(false);
    
    // If emailDetail is not provided, fetch it
    if (!emailDetail) {
      try {
        const detail = await getEmail(email.id);
        setForwardToEmailDetail(detail);
      } catch (err) {
        console.error('Failed to fetch email detail for forwarding:', err);
        setForwardToEmailDetail(null);
      }
    } else {
      setForwardToEmailDetail(emailDetail);
    }
    
    setComposeOpen(true);
  };

  const handleEmailSent = () => {
    setComposeOpen(false);
    setReplyToEmail(null);
    setForwardToEmail(null);
  };

  const handleAddContactFromEmail = useCallback(async (email: string, name: string) => {
    try {
      // First check if contact already exists
      const existingContact = await db.getContactByEmail(email);
      if (existingContact.data) {
        // Contact already exists, no need to add
        return;
      }

      // Find contacts with similar names
      const findSimilarContacts = (contactName: string, allContacts: Contact[]) => {
        const normalizedName = contactName.toLowerCase().trim();
        const nameWords = normalizedName.split(' ').filter(word => word.length > 2);
        
        return allContacts.filter(contact => {
          const contactName = contact.name.toLowerCase().trim();
          const contactWords = contactName.split(' ').filter(word => word.length > 2);
          
          // Check for exact match
          if (contactName === normalizedName) return true;
          
          // Check for partial word matches
          const matchingWords = nameWords.filter(word => 
            contactWords.some(contactWord => 
              contactWord.includes(word) || word.includes(contactWord)
            )
          );
          
          // Consider similar if at least 50% of words match
          return matchingWords.length >= Math.ceil(nameWords.length / 2);
        });
      };

      // Get all contacts to check for similar names
      const allContactsResult = await db.getContacts();
      if (allContactsResult.data) {
        const similarContacts = findSimilarContacts(name, allContactsResult.data);
        
        if (similarContacts.length > 0) {
          // For now, just create the contact anyway
          // In the future, could show a dialog like GmailInbox does
          console.log('Similar contacts found:', similarContacts);
        }
      }

      // Create new contact
      const newContact = await db.createContact({
        name,
        email,
        tags: {},
        associated_contacts: [],
        relationship_types: {},
        user_id: '', // This will be set by the database function
      });

      if (newContact.data) {
        // Reload contacts to update the UI
        const contactsResult = await db.getContacts();
        if (contactsResult.data) {
          setContacts(contactsResult.data);
        }
        
        // Trigger networking suggestions recalculation
        setTimeout(async () => {
          try {
            await db.recalculateNetworkingSuggestions();
          } catch (err) {
            console.error('Error triggering networking suggestions recalculation:', err);
          }
        }, 2000);
      } else {
        console.error('Failed to add contact:', newContact.error);
      }
    } catch (err) {
      console.error('Error adding contact:', err);
    }
  }, [db]);

  const handleSyncToEvent = (emailId: string, contactId?: string) => {
    // Handle syncing email to event
    console.log('Syncing email to event:', emailId, contactId);
  };

  const goalCategoriesComplete = (['financial', 'professional', 'personal'] as GoalCategory[]).every(category =>
    userGoals.some(goal => goal.category === category && goal.title && goal.title.trim().length > 0)
  );

  const profileComplete = Boolean(
    userProfile &&
    userProfile.full_name &&
    userProfile.preferred_email &&
    userProfile.interests &&
    userProfile.interests.length > 0 &&
    userProfile.bio &&
    userProfile.bio.trim().length > 0 &&
    goalCategoriesComplete
  );

  const profileFlags = [
    Boolean(userProfile?.full_name && userProfile?.preferred_email),
    Boolean(userProfile?.interests && userProfile.interests.length > 0),
    Boolean(userProfile?.bio && userProfile.bio.trim().length > 0),
    goalCategoriesComplete,
  ];
  const profileCompletedCount = profileFlags.filter(Boolean).length;
  const profileTotal = profileFlags.length;
  const profileProgress = profileTotal > 0 ? (profileCompletedCount / profileTotal) * 100 : 100;
  const showProfileProgress = Boolean(userProfile) && !profileComplete;

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  // Get recent contacts (last 5)
  const recentContacts = contacts
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, recentContactsLimit)
    .map(contact => ({
      ...contact,
      lastContact: 'Recently', // TODO: Implement last contact tracking
      score: contact.relevance_score || 0,
    }));

  // Get contacts with most active initiatives
  const contactsWithMostInitiatives = contacts
    .map(contact => ({
      ...contact,
      initiativeCount: initiatives.filter(i => i.status === 'Active' && i.contacts.includes(contact.id)).length
    }))
    .sort((a, b) => b.initiativeCount - a.initiativeCount)
    .slice(0, activeContactsLimit);

  // Get active initiatives with most contacts
  const initiativesWithMostContacts = initiatives
    .filter(initiative => initiative.status === 'Active')
    .map(initiative => ({
      ...initiative,
      contactCount: initiative.contacts.length
    }))
    .sort((a, b) => b.contactCount - a.contactCount)
    .slice(0, topInitiativesLimit);

  // Compute recent activity from contacts, initiatives, events, and tasks
  const activityItems: ActivityItem[] = [
    ...contacts.map((c): ActivityItem => ({
      id: c.id,
      type: 'contact' as const,
      action: c.updated_at && c.updated_at !== c.created_at ? 'Updated contact' : 'Added contact',
      name: c.name,
      timestamp: c.updated_at || c.created_at,
    })),
    ...initiatives.map((i): ActivityItem => ({
      id: i.id,
      type: 'initiative' as const,
      action: i.updated_at && i.updated_at !== i.created_at ? 'Updated initiative' : 'Created initiative',
      name: i.name,
      timestamp: i.updated_at || i.created_at,
    })),
    ...events.map((e): ActivityItem => ({
      id: e.id,
      type: 'event' as const,
      action: 'Created event',
      name: e.title,
      timestamp: e.created_at,
      metadata: { contact_id: e.contact_id },
    })),
    ...tasks.map((t): ActivityItem => ({
      id: t.id,
      type: 'task' as const,
      action: t.updated_at && t.updated_at !== t.created_at ? 'Updated task' : 'Created task',
      name: t.title,
      timestamp: t.updated_at || t.created_at,
    })),
  ]
    .filter(item => item.timestamp)
    .sort((a, b) => new Date(b.timestamp!).getTime() - new Date(a.timestamp!).getTime())
    .slice(0, recentActivityLimit);

  // Calculate completeness scores and get incomplete items
  const incompleteItems = [
    ...contacts.map(contact => ({
      id: contact.id,
      type: 'contact',
      name: contact.name,
      completeness: calculateContactCompleteness(contact),
      connectionCount: initiatives.filter(i => i.status === 'Active' && i.contacts.includes(contact.id)).length
    })),
    ...initiatives.map(initiative => ({
      id: initiative.id,
      type: 'initiative',
      name: initiative.name,
      completeness: calculateInitiativeCompleteness(initiative),
      connectionCount: initiative.contacts.length
    }))
  ]
    .sort((a, b) => {
      // First sort by connection count (ascending)
      if (a.connectionCount !== b.connectionCount) {
        return a.connectionCount - b.connectionCount;
      }
      // Then by completeness (ascending)
      return a.completeness - b.completeness;
    })
    .slice(0, incompleteItemsLimit);

  function calculateContactCompleteness(contact: Contact): number {
    let score = 0;
    let total = 0;

    // Required fields
    if (contact.name) score += 1; total += 1;
    if (contact.email) score += 1; total += 1;
    if (contact.phone) score += 1; total += 1;
    // Note: Company removed - use organizations instead (would require additional query to check)
    if (contact.title) score += 1; total += 1;
    if (contact.description) score += 1; total += 1;
    
    // Connection strength (weighted more heavily) - only count active initiatives
    const initiativeCount = initiatives.filter(i => i.status === 'Active' && i.contacts.includes(contact.id)).length;
    score += Math.min(initiativeCount / 3, 1) * 2; total += 2;

    return (score / total) * 100;
  }

  function calculateInitiativeCompleteness(initiative: Initiative): number {
    let score = 0;
    let total = 0;

    // Required fields
    if (initiative.name) score += 1; total += 1;
    if (initiative.description) score += 1; total += 1;
    if (initiative.status) score += 1; total += 1;
    if (initiative.start_date) score += 1; total += 1;
    
    // Connection strength (weighted more heavily)
    score += Math.min(initiative.contacts.length / 3, 1) * 2; total += 2;

    return (score / total) * 100;
  }

  return (
    <DashboardLayout title={`${user?.email?.split('@')[0]}'s Workspace`}>
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 }, backgroundColor: 'background.default' }}>
        {showProfileProgress && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Profile completion {Math.round(profileProgress)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={profileProgress}
              sx={{ mt: 1, height: 6, borderRadius: 999 }}
            />
          </Box>
        )}
        {userProfile && !profileComplete && (
          <Box sx={{ mb: 2 }}>
            <OnboardingChecklist profileState={profileState} onGoalsUpdated={loadUserGoals} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setOpenContactModal(true)}
              sx={{ borderRadius: 1, fontWeight: 500, fontSize: '0.875rem', px: 2, py: 0.5 }}
            >
              New Contact
            </Button>
          </Box>
        </Box>

        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: 2,
          '& > *': {
            minWidth: 0
          }
        }}>
          {/* Important Emails */}
          {gmailConnected && (
            <Box sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              p: 1.5,
              gridColumn: { xs: '1', sm: 'span 1' }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>
                  Important Emails
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => navigate('/email')} 
                    sx={{ color: 'text.secondary' }}
                    title="View All Emails"
                  >
                    <MailIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <List sx={{ p: 0 }}>
                {importantEmails.length === 0 ? (
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary={
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          No important emails
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  <>
                    {importantEmails.map((email, idx) => (
                      <React.Fragment key={email.id}>
                        <ListItem
                          sx={{ 
                            py: 0.5, 
                            cursor: 'pointer', 
                            '&:hover': { 
                              backgroundColor: 'action.hover'
                            } 
                          }}
                          onClick={() => handleEmailClick(email)}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {email.isStarred && (
                                  <StarIcon sx={{ fontSize: '0.75rem', color: 'warning.main' }} />
                                )}
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: 'text.primary', 
                                    fontWeight: email.isUnread ? 600 : 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {email.subject || '(No Subject)'}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                  }}
                                >
                                  {email.from}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  {new Date(email.date).toLocaleDateString()}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                    <ListItem
                      onClick={() => navigate('/email')}
                      sx={{ 
                        py: 0.5, 
                        cursor: 'pointer', 
                        '&:hover': { 
                          backgroundColor: 'action.hover'
                        } 
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'primary.main',
                              fontStyle: 'italic',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              fontSize: '0.75rem'
                            }}
                          >
                            View All Emails
                          </Typography>
                        }
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </Box>
          )}

          {/* Contacts Tile */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
              {contacts.length}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 2 }}>
              Contacts
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/contacts')}
              sx={{ 
                borderRadius: 1, 
                fontWeight: 500, 
                fontSize: '0.75rem', 
                px: 2, 
                py: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              View Contacts
            </Button>
          </Box>

          {/* Initiatives Tile */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
              {initiatives.filter(i => i.status === 'Active').length}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 2 }}>
              Initiatives
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/initiatives?status=Active')}
              sx={{ 
                borderRadius: 1, 
                fontWeight: 500, 
                fontSize: '0.75rem', 
                px: 2, 
                py: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              View Initiatives
            </Button>
          </Box>

          {/* Tasks Tile */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
              {tasks.filter(t => t.status === 'pending' || t.status === 'in_progress').length}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 2 }}>
              Tasks
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/tasks')}
              sx={{ 
                borderRadius: 1, 
                fontWeight: 500, 
                fontSize: '0.75rem', 
                px: 2, 
                py: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              View Tasks
            </Button>
          </Box>

          {/* Quant Updates */}
          <QuantUpdates events={events} />

          {/* Ask Quant */}
          <AskQuant />



          {/* Recent Activity Feed */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            p: 2
          }}>
            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Recent Activity</Typography>
            <List sx={{ p: 0 }}>
              {activityItems.length === 0 ? (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="No recent activity" />
                </ListItem>
              ) : (
                <>
                  {activityItems.map((activity, idx) => (
                    <React.Fragment key={activity.id + activity.timestamp}>
                      <ListItem 
                        sx={{ 
                          py: 0.5, 
                          alignItems: 'flex-start',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => {
                          if (activity.type === 'event') {
                            // For events, navigate to the contact's page
                            navigate(`/contacts/${activity.metadata?.contact_id}`);
                          } else {
                            navigate(`/${activity.type}s/${activity.id}`);
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                              {activity.action} <strong>{activity.name}</strong>
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ''}
                            </Typography>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                  <ListItem
                    onClick={() => setRecentActivityLimit(prev => prev + 5)}
                    sx={{ 
                      py: 0.5, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        backgroundColor: 'action.hover'
                      } 
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'primary.main',
                            fontStyle: 'italic',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: '0.75rem'
                          }}
                        >
                          Load More
                        </Typography>
                      }
                    />
                  </ListItem>
                </>
              )}
            </List>
          </Box>

          {/* Recent Contacts */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            p: 1.5,
            gridColumn: { xs: '1', sm: 'span 1' }
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>Recent Contacts</Typography>
              <IconButton size="small" onClick={() => navigate('/contacts')} sx={{ color: 'text.secondary' }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
            <List sx={{ p: 0 }}>
              {recentContacts.map((contact, idx) => (
                <React.Fragment key={contact.id}>
                  <ListItem
                    onClick={() => handleContactClick(contact.id)}
                    sx={{ 
                      py: 0.5, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        backgroundColor: 'action.hover'
                      } 
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {contact.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {contact.email}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              <ListItem
                onClick={() => setRecentContactsLimit(prev => prev + 5)}
                sx={{ 
                  py: 0.5, 
                  cursor: 'pointer', 
                  '&:hover': { 
                    backgroundColor: 'action.hover'
                  } 
                }}
              >
                <ListItemText
                  primary={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'primary.main',
                        fontStyle: 'italic',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem'
                      }}
                    >
                      Load More
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>

          {/* Contacts with Most Initiatives */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            p: 1.5,
            gridColumn: { xs: '1', sm: 'span 1' }
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>Most Active Contacts</Typography>
              <IconButton size="small" onClick={() => navigate('/contacts')} sx={{ color: 'text.secondary' }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
            <List sx={{ p: 0 }}>
              {contactsWithMostInitiatives.map((contact, idx) => (
                <React.Fragment key={contact.id}>
                  <ListItem
                    onClick={() => handleContactClick(contact.id)}
                    sx={{ 
                      py: 0.5, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        backgroundColor: 'action.hover'
                      } 
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {contact.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {contact.initiativeCount} initiatives
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              <ListItem
                onClick={() => setActiveContactsLimit(prev => prev + 5)}
                sx={{ 
                  py: 0.5, 
                  cursor: 'pointer', 
                  '&:hover': { 
                    backgroundColor: 'action.hover'
                  } 
                }}
              >
                <ListItemText
                  primary={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'primary.main',
                        fontStyle: 'italic',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem'
                      }}
                    >
                      Load More
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>

          {/* Initiatives with Most Contacts */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            p: 1.5,
            gridColumn: { xs: '1', sm: 'span 1' }
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>Top Initiatives</Typography>
              <IconButton size="small" onClick={() => navigate('/initiatives')} sx={{ color: 'text.secondary' }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
            <List sx={{ p: 0 }}>
              {initiativesWithMostContacts.map((initiative, idx) => (
                <React.Fragment key={initiative.id}>
                  <ListItem
                    onClick={() => navigate(`/initiatives/${initiative.id}`)}
                    sx={{ 
                      py: 0.5, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        backgroundColor: 'action.hover'
                      } 
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {initiative.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {initiative.contactCount} contacts
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              <ListItem
                onClick={() => setTopInitiativesLimit(prev => prev + 5)}
                sx={{ 
                  py: 0.5, 
                  cursor: 'pointer', 
                  '&:hover': { 
                    backgroundColor: 'action.hover'
                  } 
                }}
              >
                <ListItemText
                  primary={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'primary.main',
                        fontStyle: 'italic',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem'
                      }}
                    >
                      Load More
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>

          {/* Incomplete Items Tile */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            p: 1.5,
            gridColumn: { xs: '1', sm: 'span 1' }
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>
                Low Valence
              </Typography>
              <Box sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                <IconButton 
                  size="small" 
                  onClick={() => setOpenValenceModal(true)}
                  sx={{ color: 'text.secondary' }}
                >
                  <BarChartIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <List sx={{ p: 0 }}>
              {incompleteItems.length === 0 ? (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="All items are complete!" />
                </ListItem>
              ) : (
                <>
                  {incompleteItems.map((item, idx) => (
                    <React.Fragment key={item.id}>
                      <ListItem
                        onClick={() => navigate(`/${item.type}s/${item.id}`)}
                        sx={{ 
                          py: 0.5, 
                          cursor: 'pointer', 
                          '&:hover': { 
                            backgroundColor: 'action.hover'
                          } 
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                {item.name}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                  <ListItem
                    onClick={() => setIncompleteItemsLimit(prev => prev + 5)}
                    sx={{ 
                      py: 0.5, 
                      cursor: 'pointer', 
                      '&:hover': { 
                        backgroundColor: 'action.hover'
                      } 
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'primary.main',
                            fontStyle: 'italic',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: '0.75rem'
                          }}
                        >
                          Load More
                        </Typography>
                      }
                    />
                  </ListItem>
                </>
              )}
            </List>
          </Box>
        </Box>
      </Box>

      <ContactModal
        open={openContactModal}
        onClose={() => setOpenContactModal(false)}
        onSubmit={handleAddContact}
      />

      {/* Email Compose Modal */}
      <EmailCompose
        open={composeOpen}
        onClose={() => {
          setComposeOpen(false);
          setReplyToEmail(null);
          setForwardToEmail(null);
          setForwardToEmailDetail(null);
        }}
        onSent={handleEmailSent}
        replyTo={replyToEmail || undefined}
        forwardTo={forwardToEmail || undefined}
        forwardToDetail={forwardToEmailDetail || undefined}
      />

      {/* Email Detail Modal */}
      {selectedEmail && (
        <EmailDetail
          open={emailDetailOpen}
          onClose={handleEmailDetailClose}
          email={selectedEmail}
          onReply={handleReply}
          onForward={handleForward}
          onAddContact={handleAddContactFromEmail}
          onSyncToEvent={handleSyncToEvent}
        />
      )}

      <Modal
        open={openValenceModal}
        onClose={() => setOpenValenceModal(false)}
        aria-labelledby="valence-heatmap-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box sx={{
          width: '100%',
          maxWidth: '90vw',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Entity Valence</Typography>
          <EntityValence contacts={contacts} initiatives={initiatives} />
        </Box>
      </Modal>

    </DashboardLayout>
  );
} 